import { format as formatDate, parseISO } from 'date-fns';

/**
 * Display timezone. The spec (see the "Table" section) fixes one timezone for every admin, regardless of
 * where they sit. The zone itself is a server-side environment variable, served at runtime
 * by `GET /admin/api/config` (functional spec, API Contract Reference — Runtime configuration) rather than baked into the bundle, so
 * changing it is a restart and not a frontend rebuild.
 *
 * This constant is only the stand-in for the window before that config has loaded; every
 * helper below takes the zone as an argument, so there is no module-level mutable state.
 * Components read the resolved zone through `useTimeFormat()` (`src/lib/timezone.tsx`).
 */
export const FALLBACK_TIMEZONE = 'America/Toronto';

/**
 * `Intl.DateTimeFormat` construction is expensive, so formatters are memoised per zone.
 * The cache is derived data keyed by its input — never state a component writes.
 */
const FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  const cached = FORMATTER_CACHE.get(timeZone);
  if (cached) return cached;
  const created = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    // `short`, not `shortOffset`: the spec's "Filter bar" section asks for the zone's own abbreviation
    // ("e.g. EST/EDT"), which also says whether daylight time was in force at that
    // instant. `shortOffset` rendered every timestamp as `GMT-4`, leaving the reader to
    // work that out — and one offset can belong to several zones.
    timeZoneName: 'short',
  });
  FORMATTER_CACHE.set(timeZone, created);
  return created;
}

interface ZonedParts {
  date: string;
  time: string;
  offset: string;
}

function zonedParts(iso: string, timeZone: string): ZonedParts | null {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  const parts = partsFormatter(timeZone).formatToParts(parsed);
  const pick = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';
  const hour = pick('hour') === '24' ? '00' : pick('hour');
  return {
    date: `${pick('year')}-${pick('month')}-${pick('day')}`,
    time: `${hour}:${pick('minute')}:${pick('second')}`,
    offset: pick('timeZoneName') || 'UTC',
  };
}

function todayInZone(timeZone: string): string {
  const parts = zonedParts(new Date().toISOString(), timeZone);
  return parts?.date ?? '';
}

/** `YYYY-MM-DD HH:mm:ss UTC±HH:mm` — the unambiguous form used in detail panels. */
export function formatTimestamp(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return '—';
  const parts = zonedParts(iso, timeZone);
  if (!parts) return '—';
  return `${parts.date} ${parts.time} ${parts.offset}`;
}

/**
 * Table form (see the "Table" section): `HH:MM:SS` for entries from today in the display timezone,
 * `YYYY-MM-DD HH:MM:SS` otherwise. The offset always travels in the tooltip.
 */
export function formatTableTime(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return '—';
  const parts = zonedParts(iso, timeZone);
  if (!parts) return '—';
  return parts.date === todayInZone(timeZone) ? parts.time : `${parts.date} ${parts.time}`;
}

/** `YYYY-MM-DD` only — the ADDED / CREATED columns. */
export function formatDay(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return '—';
  const parts = zonedParts(iso, timeZone);
  return parts?.date ?? '—';
}

/** The raw value, kept verbatim for `title` tooltips so nothing is ever ambiguous. */
export function rawTimestampTitle(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return '';
  const parts = zonedParts(iso, timeZone);
  if (!parts) return iso;
  return `${parts.date} ${parts.time} ${parts.offset} (${timeZone}) — raw: ${iso}`;
}

/** Local-date helper for the date pickers, formatted with date-fns. */
export function isoToDateInput(iso: string): string {
  try {
    return formatDate(parseISO(iso), 'yyyy-MM-dd');
  } catch {
    return '';
  }
}

const UNITS = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;

/** Human-readable size: `4.7 KB`, `12.3 MB`, `0 bytes`. */
export function formatBytes(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  if (value < 1024) return `${value} ${value === 1 ? 'byte' : 'bytes'}`;
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < UNITS.length - 1) {
    size /= 1024;
    unit += 1;
  }
  const decimals = size >= 100 ? 0 : 1;
  return `${size.toFixed(decimals)} ${UNITS[unit]}`;
}

export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
}
